import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { canAttachRevision, missingReferences, requiredReferenceIds } from './pocketImportModel';
import { referenceGroups } from './pocketTrace';

const DEDUP_LABELS = {
  alreadyImported: 'Déjà importé dans ce document',
  newerRevision: 'Version plus récente d’un élément déjà importé',
  olderRevision: 'Version plus ancienne d’un élément déjà importé'
};

const formatDate = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const primaryTime = (display) => {
  const times = display?.times || [];
  const primary = times.find((t) => t.primary && t.state === 'set') || times.find((t) => t.state === 'set');
  return primary ? primary.display : 'Heure non renseignée';
};

function TargetChip({ kind, target }) {
  if (target.status === 'freeText' && kind === 'event') {
    return <Chip size="small" color="primary" variant="outlined" label={`→ ${target.title} (rubrique vierge rédigée)`} />;
  }
  if (target.status === 'freeText') return <Chip size="small" label="Texte libre" variant="outlined" />;
  if (target.status === 'unresolved') return <Chip size="small" color="warning" label="Événement Pocket à rattacher" />;
  return <Chip size="small" color="primary" variant="outlined" label={`→ ${target.title} (à générer)`} />;
}

function ItemRow({ item, choice, onChange, palette: c }) {
  const [open, setOpen] = useState(false);
  const isNew = item.dedup?.status === 'new';
  const variants = (item.target?.variants || []).filter((v) => v.available);
  return (
    <Box sx={{ borderBottom: `1px solid ${c.border}`, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Checkbox
          checked={choice.selected}
          onChange={(e) => onChange({ ...choice, selected: e.target.checked })}
          inputProps={{ 'aria-label': `Importer ${item.display?.label || item.display?.title}` }}
          sx={{ mt: -0.5 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: c.textSecondary }}>{primaryTime(item.display)}</Typography>
          <Typography sx={{ fontWeight: 600, color: c.text }}>
            {item.kind === 'freeText' ? (item.display?.title || 'Texte libre') : item.display?.label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
            <TargetChip kind={item.kind} target={item.target} />
            {item.splitCount > 1 && <Chip size="small" label={`${item.splitCount} rubriques (une par personne)`} />}
            {!isNew && <Chip size="small" color="secondary" label={DEDUP_LABELS[item.dedup.status]} />}
            {item.unmapped?.length > 0 && <Chip size="small" variant="outlined" label={`${item.unmapped.length} donnée(s) sans champ cible`} />}
          </Box>
          {item.target?.status === 'unresolved' && item.target.reason && (
            <Typography variant="body2" sx={{ mt: 0.5, color: c.textSecondary }}>{item.target.reason}</Typography>
          )}
          {!isNew && choice.selected && (
            <FormControl size="small" sx={{ mt: 1, minWidth: 280 }}>
              <InputLabel id={`decision-${item.id}`}>Action</InputLabel>
              <Select
                labelId={`decision-${item.id}`}
                label="Action"
                value={choice.decision}
                onChange={(e) => onChange({ ...choice, decision: e.target.value })}
              >
                <MenuItem value="newRubric">Créer une nouvelle rubrique</MenuItem>
                {canAttachRevision(item) && <MenuItem value="attachRevision">Joindre à la rubrique déjà importée</MenuItem>}
              </Select>
            </FormControl>
          )}
          {!isNew && item.dedup.previous && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: c.textSecondary }}>
              Import précédent : révision {item.dedup.previous.importedRevision} le {formatDate(item.dedup.previous.importedAt)}
              {item.dedup.previous.rubricStillPresent ? '' : ' (rubrique supprimée depuis)'}. Les modifications faites dans BlueNote ne sont jamais écrasées.
            </Typography>
          )}
          {item.target?.status === 'mapped' && variants.length > 1 && (
            <FormControl size="small" sx={{ mt: 1, minWidth: 280 }}>
              <InputLabel id={`variant-${item.id}`}>Rubrique cible</InputLabel>
              <Select
                labelId={`variant-${item.id}`}
                label="Rubrique cible"
                value={choice.variant || item.target.variant}
                onChange={(e) => onChange({ ...choice, variant: e.target.value })}
              >
                {variants.map((v) => <MenuItem key={v.id} value={v.id}>{v.title}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Collapse in={open} unmountOnExit>
            <Box sx={{ mt: 1, pl: 1, borderLeft: `3px solid ${c.border}` }}>
              {(item.display?.times || []).filter((t) => t.state !== 'absent').map((t) => (
                <Typography key={t.key} variant="body2">{t.label} : {t.display}</Typography>
              ))}
              {(item.display?.answers || []).map((a) => <Typography key={a.key} variant="body2">{a.label} : {a.display}</Typography>)}
              {referenceGroups(item.display).map((g) => (
                <Typography key={g.key} variant="body2">{g.label} : {g.refs.map((p) => p.label).join(', ')}</Typography>
              ))}
              {item.display?.notes && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.display.notesLabel} : {item.display.notes}</Typography>}
              {item.prefill?.length > 0 && (
                <>
                  <Typography variant="overline" sx={{ display: 'block', mt: 1 }}>Préremplissage du formulaire</Typography>
                  {item.prefill.map((p) => <Typography key={p.field} variant="body2">{p.label} : {p.display}</Typography>)}
                </>
              )}
              {item.unmapped?.length > 0 && item.kind !== 'freeText' && (
                <>
                  <Typography variant="overline" sx={{ display: 'block', mt: 1 }}>Sans champ cible (restera affiché avec la rubrique)</Typography>
                  {item.unmapped.map((u) => <Typography key={u.key} variant="body2">{u.label} : {u.display}</Typography>)}
                </>
              )}
            </Box>
          </Collapse>
        </Box>
        <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? 'Masquer le détail' : 'Afficher le détail'}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}

/** « Moi » : l'agent qui a encodé la mission. L'utilisateur est suggéré, jamais rattaché sans sa confirmation. */
function SelfSuggestion({ reference, value, options, onChange, palette: c }) {
  const suggested = options.find((o) => o.id === reference.suggestedEntityId);
  if (!suggested) {
    return (
      <Typography variant="body2" sx={{ width: '100%', color: c.textSecondary }}>
        Vous ne figurez pas parmi les intervenants du document : ajoutez-vous dans l’encodage puis actualisez, ou choisissez
        l’intervenant qui a encodé la mission.
      </Typography>
    );
  }
  if (value === suggested.id) {
    return (
      <Typography variant="body2" sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
        <CheckCircleOutlineIcon fontSize="small" /> Rattaché à vous-même.
      </Typography>
    );
  }
  return (
    <Alert
      severity="info"
      sx={{ width: '100%', py: 0, alignItems: 'center' }}
      action={<Button size="small" onClick={() => onChange(suggested.id)}>Oui, c’est moi</Button>}
    >
      « Moi » sur le téléphone : est-ce vous ({suggested.label}) ?
    </Alert>
  );
}

function ReferenceRow({ reference, value, options, onChange, palette: c }) {
  const empty = options.length === 0;
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', py: 1, borderBottom: `1px solid ${c.border}` }}>
      <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, color: c.text }}>{reference.label}</Typography>
        {reference.self && <Typography variant="body2" sx={{ color: c.textSecondary }}>L’agent qui a encodé la mission</Typography>}
        {reference.comment && <Typography variant="body2" sx={{ color: c.textSecondary, whiteSpace: 'pre-wrap' }}>{reference.comment}</Typography>}
      </Box>
      <FormControl size="small" sx={{ flex: '1 1 260px' }} error={!value}>
        <InputLabel id={`ref-${reference.id}`}>{reference.kind === 'person' ? 'Personne du document' : 'Intervenant du document'}</InputLabel>
        <Select
          labelId={`ref-${reference.id}`}
          label={reference.kind === 'person' ? 'Personne du document' : 'Intervenant du document'}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={empty}
        >
          <MenuItem value=""><em>Choisir…</em></MenuItem>
          {options.map((o) => <MenuItem key={o.id} value={o.id}>{o.label}{o.isSelf ? ' (vous)' : ''}{o.detail ? ` — ${o.detail}` : ''}</MenuItem>)}
        </Select>
      </FormControl>
      {reference.self && !empty && <SelfSuggestion reference={reference} value={value} options={options} onChange={onChange} palette={c} />}
      {empty && (
        <Typography variant="body2" sx={{ width: '100%', color: c.textSecondary }}>
          {reference.kind === 'person'
            ? 'Aucune personne physique encodée : ajoutez-la dans l’encodage, puis actualisez.'
            : 'Aucun intervenant encodé : ajoutez-le dans l’encodage, puis actualisez.'}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Revue d'un fichier Pocket déchiffré : choix des éléments, rattachement des références, import.
 */
function PocketImportReview({ preview, choices, setChoices, mappings, setMappings, onRefresh, refreshing, onImport, importing, importError, result, onClose, onRestart, palette: c }) {
  const [phase, setPhase] = useState('items');
  const selectedCount = useMemo(() => Object.values(choices).filter((ch) => ch.selected).length, [choices]);
  const requiredIds = useMemo(() => requiredReferenceIds(preview, choices), [preview, choices]);
  const missing = useMemo(() => missingReferences(preview, choices, mappings), [preview, choices, mappings]);

  if (result) {
    const created = result.createdRubrics || [];
    return (
      <Box sx={{ textAlign: 'center', py: 3 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main' }} />
        <Typography variant="h6" sx={{ mt: 1, color: c.text }}>
          {result.alreadyDone ? 'Cet import avait déjà été réalisé.' : `${created.length} rubrique${created.length > 1 ? 's' : ''} ajoutée${created.length > 1 ? 's' : ''} à la fin du document`}
        </Typography>
        {result.attached > 0 && (
          <Typography sx={{ color: c.textSecondary }}>{result.attached} révision(s) jointe(s) aux rubriques existantes, sans modification automatique.</Typography>
        )}
        <Typography variant="body2" sx={{ mt: 1.5, color: c.textSecondary, maxWidth: 520, mx: 'auto' }}>
          Les rubriques à générer portent la mention « Des données proviennent de BlueNote Pocket ».
          Leurs formulaires sont préremplis, sur fond bleuté : vérifiez-les avant de générer.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 3 }}>
          <Button onClick={onRestart}>Importer un autre fichier</Button>
          <Button variant="contained" onClick={onClose}>Fermer</Button>
        </Box>
      </Box>
    );
  }

  const setChoice = (id, value) => setChoices((prev) => ({ ...prev, [id]: value }));
  const setAll = (selected) => setChoices((prev) => {
    const next = { ...prev };
    preview.items.forEach((item) => {
      if (item.dedup.status === 'new') next[item.id] = { ...next[item.id], selected };
    });
    return next;
  });

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, color: c.text }}>Dossier « {preview.folder.title} »</Typography>
        <Typography variant="body2" sx={{ color: c.textSecondary }}>
          Préparé le {formatDate(preview.export.preparedAt)} — {preview.items.length} élément{preview.items.length > 1 ? 's' : ''}
        </Typography>
        {preview.alreadyImportedExport && (
          <Alert severity="info" sx={{ mt: 1 }}>Ce fichier a déjà été importé dans ce document : les éléments déjà repris ne sont pas cochés.</Alert>
        )}
        {!preview.export.catalogMatches && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Ce fichier provient d’une version différente du catalogue Pocket. Les éléments non reconnus seront à rattacher.
          </Alert>
        )}
      </Box>

      {phase === 'items' && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, color: c.textSecondary }}>
              Ordre chronologique. Les éléments sont ajoutés à la fin du document dans cet ordre.
            </Typography>
            <Button size="small" onClick={() => setAll(true)}>Tout cocher</Button>
            <Button size="small" onClick={() => setAll(false)}>Tout décocher</Button>
          </Box>
          <Box>
            {preview.items.map((item) => (
              <ItemRow key={item.id} item={item} choice={choices[item.id]} onChange={(v) => setChoice(item.id, v)} palette={c} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={onRestart}>Changer de fichier</Button>
            <Button variant="contained" disabled={selectedCount === 0} onClick={() => setPhase('references')}>
              Continuer ({selectedCount})
            </Button>
          </Box>
        </>
      )}

      {phase === 'references' && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ flex: 1, color: c.textSecondary }}>
              {requiredIds.length === 0
                ? 'Aucune personne ni aucun intervenant à rattacher pour les éléments retenus.'
                : 'Indiquez à qui correspond chaque entité utilisée par les éléments retenus.'}
            </Typography>
            <Tooltip title="Actualiser les personnes et intervenants du document">
              <span>
                <IconButton size="small" onClick={onRefresh} disabled={refreshing} aria-label="Actualiser">
                  {refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {preview.references.filter((r) => requiredIds.includes(r.id)).map((ref) => (
            <ReferenceRow
              key={ref.id}
              reference={ref}
              value={mappings[ref.id]}
              options={ref.kind === 'person' ? preview.documentEntities.personnesPhysiques : preview.documentEntities.intervenants}
              onChange={(entityId) => setMappings((prev) => ({ ...prev, [ref.id]: entityId }))}
              palette={c}
            />
          ))}
          {importError && <Alert severity="error" sx={{ mt: 2 }}>{importError}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={() => setPhase('items')} disabled={importing}>Retour</Button>
            <Button variant="contained" disabled={missing.length > 0 || importing} onClick={onImport}>
              {importing ? <CircularProgress size={20} /> : `Importer ${selectedCount} élément${selectedCount > 1 ? 's' : ''}`}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default PocketImportReview;
